import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useToast } from '@chakra-ui/react';
import { subscribeToLoadingState, subscribeToErrors } from './loaderManager';

const LoaderContext = createContext();

export const LoaderProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    // Subscribe to loading state changes from loaderManager
    const unsubscribeLoading = subscribeToLoadingState(setIsLoading);

    // Subscribe to error messages from loaderManager
    const unsubscribeErrors = subscribeToErrors(
      (errorMessage, errorDetails) => {
        toast({
          title: errorMessage,
          description:
            errorDetails?.message || errorDetails?.data?.message || null,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    );

    // Clean up subscriptions on unmount
    return () => {
      if (unsubscribeLoading) {
        unsubscribeLoading();
      }
      if (unsubscribeErrors) {
        unsubscribeErrors();
      }
    };
  }, [toast]);

  const contextValue = useMemo(
    () => ({
      isLoading,
      setIsLoading,
    }),
    [isLoading, setIsLoading]
  );

  return (
    <LoaderContext.Provider value={contextValue}>
      {children}
    </LoaderContext.Provider>
  );
};

export const useLoader = () => {
  const context = useContext(LoaderContext);
  if (!context) {
    throw new Error('useLoader must be used within a LoaderProvider');
  }
  return context;
};
